import React, { useState, useEffect, useCallback } from 'react';
import documentTypeApi, { DocumentType } from '../../api/documentTypeApi';
import archiveCategoryApi, { ArchiveCategory } from '../../api/archiveCategoryApi';
import { useToast } from '../../context/ToastContext';
import DocumentTypeHeader from '../../components/archive/document-type/DocumentTypeHeader';
import DocumentTypeSearch from '../../components/archive/document-type/DocumentTypeSearch';
import DocumentTypeTable from '../../components/archive/document-type/DocumentTypeTable';
import DocumentTypeModal from '../../components/archive/document-type/DocumentTypeModal';
import DeleteDocumentTypeDialog from '../../components/archive/document-type/DeleteDocumentTypeDialog';

const initialDocumentTypeData: DocumentType = {
  document_code: '',
  document_name: '',
  category_id: 0,
  is_mandatory: false,
  requires_validation: true,
  requires_number: false,
  requires_expiry: false,
  allowed_formats: ['PDF', 'JPG', 'JPEG', 'PNG'],
  max_file_size_mb: 5,
  is_active: true,
};

export default function DocumentTypeList() {
  const { showSuccess, showError } = useToast();
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [categories, setCategories] = useState<ArchiveCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modal, setModal] = useState<{ open: boolean; data: DocumentType | null }>({
    open: false,
    data: null,
  });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    documentType: DocumentType | null;
  }>({
    open: false,
    documentType: null,
  });

  const loadData = useCallback(async () => {
    try {
      const [docTypes, cats] = await Promise.all([
        documentTypeApi.getAll(),
        archiveCategoryApi.getAll(),
      ]);
      setDocumentTypes(docTypes);
      setCategories(cats);
    } catch (error: any) {
      showError('Gagal memuat data: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openModal = (item: DocumentType | null = null) => {
    setModal({
      open: true,
      data: item || { ...initialDocumentTypeData },
    });
  };

  const closeModal = () => {
    setModal({ open: false, data: null });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal.data) return;

    setSaving(true);
    try {
      if (modal.data.id) {
        await documentTypeApi.update(modal.data.id, modal.data);
        showSuccess('Jenis dokumen berhasil diperbarui');
      } else {
        await documentTypeApi.create(modal.data);
        showSuccess('Jenis dokumen berhasil ditambahkan');
      }
      await loadData();
      closeModal();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || 'Gagal menyimpan jenis dokumen';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (documentType: DocumentType) => {
    setDeleteDialog({ open: true, documentType });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.documentType?.id) return;

    try {
      await documentTypeApi.delete(deleteDialog.documentType.id);
      showSuccess('Jenis dokumen berhasil dihapus');
      await loadData();
      setDeleteDialog({ open: false, documentType: null });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || 'Gagal menghapus jenis dokumen';
      showError(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, documentType: null });
  };

  const handleDataChange = (data: DocumentType) => {
    setModal(prev => ({ ...prev, data }));
  };

  const filteredDocTypes = documentTypes.filter(
    d =>
      d.document_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.document_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DocumentTypeHeader onAddClick={() => openModal(null)} />

      <DocumentTypeSearch searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      <DocumentTypeTable
        documentTypes={filteredDocTypes}
        categories={categories}
        onEdit={openModal}
        onDelete={handleDeleteClick}
      />

      <DocumentTypeModal
        open={modal.open}
        data={modal.data}
        categories={categories}
        saving={saving}
        onClose={closeModal}
        onSave={handleSave}
        onDataChange={handleDataChange}
      />

      <DeleteDocumentTypeDialog
        open={deleteDialog.open}
        documentTypeName={deleteDialog.documentType?.document_name}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
